import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardBody, CardHeader, Button, Spinner, Alert, Table, Badge, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { APIClient } from '../../../helpers/api_helper';
import CreateEndpointModal from './CreateEndpointModal';
import CodeExamplesModal from './CodeExamplesModal';
import EndpointHelpModal from './EndpointHelpModal';

interface DocProcessor {
    processorId: number;
    companyId: number;
    projectId: number;
    title: string;
    description: string | null;
    endpointSlug: string;
    jsonFormatData: string;
    prompt: string;
    llmId: number;
    status: number;
    sourceType?: number;
    envId?: number;
    linkedProcessorId?: number | null;
    publishedDate?: string | null;
    createdAt: string | null;
    updatedAt: string | null;
}

interface SandboxEndpointsTableProps {
    projectId: string | undefined;
}

const SandboxEndpointsTable: React.FC<SandboxEndpointsTableProps> = ({ projectId }) => {
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [docProcessors, setDocProcessors] = useState<DocProcessor[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedEndpoint, setSelectedEndpoint] = useState<DocProcessor | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [endpointToDelete, setEndpointToDelete] = useState<DocProcessor | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isInactivating, setIsInactivating] = useState(false);
    const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
    const [endpointToPublish, setEndpointToPublish] = useState<DocProcessor | null>(null);
    const [isPublishing, setIsPublishing] = useState(false);
    const [isCodeExamplesModalOpen, setIsCodeExamplesModalOpen] = useState(false);
    const [endpointForCodeExamples, setEndpointForCodeExamples] = useState<DocProcessor | null>(null);
    const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);

    const apiClient = useMemo(() => new APIClient(), []);

    const handleEditEndpoint = (endpoint: DocProcessor) => {
        setSelectedEndpoint(endpoint);
        setIsEditMode(true);
        setIsCreateModalOpen(true);
    };

    const handleDeleteEndpoint = (endpoint: DocProcessor) => {
        setEndpointToDelete(endpoint);
        setIsDeleteModalOpen(true);
    };

    const handlePublishEndpoint = (endpoint: DocProcessor) => {
        setEndpointToPublish(endpoint);
        setIsPublishModalOpen(true);
    };

    const handleShowCodeExamples = (endpoint: DocProcessor) => {
        setEndpointForCodeExamples(endpoint);
        setIsCodeExamplesModalOpen(true);
    };

    const closeCodeExamplesModal = () => {
        setIsCodeExamplesModalOpen(false);
        setEndpointForCodeExamples(null);
    };

    const confirmPublishEndpoint = async () => {
        if (!endpointToPublish) return;
        
        setIsPublishing(true);
        try {
            console.log('Publishing endpoint:', endpointToPublish.processorId);
            await apiClient.create(`/docprocessor/publish/${endpointToPublish.processorId}`, {});
            
            // Refresh the data after successful publishing
            await fetchDocProcessors();
            
            // Close the modal
            setIsPublishModalOpen(false);
            setEndpointToPublish(null);
        } catch (error) {
            console.error('Error publishing endpoint:', error);
        } finally {
            setIsPublishing(false);
        }
    };

    const closePublishModal = () => {
        setIsPublishModalOpen(false);
        setEndpointToPublish(null);
    };

    const confirmDeleteEndpoint = async () => {
        if (!endpointToDelete) return;
        
        setIsDeleting(true);
        try {
            console.log('Deleting endpoint:', endpointToDelete.processorId);
            await apiClient.delete(`/docprocessor/${endpointToDelete.processorId}`);
            
            await fetchDocProcessors();
            setIsDeleteModalOpen(false);
            setEndpointToDelete(null);
        } catch (error) {
            console.error('Error deleting endpoint:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    const closeDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setEndpointToDelete(null);
    };

    const handleInactiveEndpoint = async () => {
        if (!endpointToDelete) return;
        
        setIsInactivating(true);
        try {
            console.log('Inactivating endpoint:', endpointToDelete.processorId);
            await apiClient.put(`/docprocessor/${endpointToDelete.processorId}`, { status: 0 });
            
            await fetchDocProcessors();
            setIsDeleteModalOpen(false);
            setEndpointToDelete(null);
        } catch (error) {
            console.error('Error inactivating endpoint:', error);
        } finally {
            setIsInactivating(false);
        }
    };

    const handleCreateEndpoint = () => {
        setSelectedEndpoint(null);
        setIsEditMode(false);
        setIsCreateModalOpen(true);
    };

    const fetchDocProcessors = async () => {
        if (!projectId) return;
        
        setIsLoading(true);
        setError(null);
        
        try {
            // Fetch sandbox endpoints (envId = 1)
            const response = await apiClient.get(`/docprocessor/project/${projectId}/1`);
            console.log('API Response:', response);
            
            const data = Array.isArray(response?.data) ? response.data : (Array.isArray(response) ? response : []);
            setDocProcessors(data);
        } catch (err) {
            console.error('Error fetching doc processors:', err);
            setError('Failed to load endpoints. Please try again.');
            setDocProcessors([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchDocProcessors();
    }, [projectId]);

    const handleModalClose = () => {
        setIsCreateModalOpen(false);
        setSelectedEndpoint(null);
        setIsEditMode(false);
        fetchDocProcessors();
    };

    const formatDate = (dateString: string) => {
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return dateString;
        }
    };

    const getStatusBadge = (status: any) => {
        if (typeof status === 'number') {
            switch (status) {
                case 0:
                    return <Badge color="secondary">Inactive</Badge>;
                case 1:
                    return <Badge color="success">Active</Badge>;
                case 2:
                    return <Badge color="primary">Published</Badge>;
                default:
                    return <Badge color="info">Unknown ({status})</Badge>;
            }
        }
        
        const statusStr = typeof status === 'string' ? status.toLowerCase() : String(status || '').toLowerCase();
        
        switch (statusStr) {
            case 'inactive':
            case '0':
                return <Badge color="secondary">Inactive</Badge>;
            case 'active':
            case '1':
                return <Badge color="success">Active</Badge>;
            case 'published':
            case '2':
                return <Badge color="primary">Published</Badge>;
            case 'error':
                return <Badge color="danger">Error</Badge>;
            case 'pending':
                return <Badge color="warning">Pending</Badge>;
            default:
                return <Badge color="info">{status || 'Unknown'}</Badge>;
        }
    };

    const renderTableContent = () => {
        if (isLoading) {
            return (
                <div className="text-center py-4">
                    <Spinner color="primary" />
                    <p className="text-muted mt-2">Loading sandbox endpoints...</p>
                </div>
            );
        }

        if (error) {
            return (
                <Alert color="danger" className="mb-0">
                    <div className="d-flex align-items-center">
                        <i className="ri-error-warning-line me-2"></i>
                        {error}
                        <Button 
                            color="link" 
                            size="sm" 
                            onClick={fetchDocProcessors}
                            className="ms-auto text-decoration-none"
                        >
                            <i className="ri-refresh-line me-1"></i>
                            Retry
                        </Button>
                    </div>
                </Alert>
            );
        }

        if (docProcessors.length === 0) {
            return (
                <div className="text-center py-5">
                    <div className="mb-3">
                        <i className="ri-file-list-2-line text-muted" style={{ fontSize: '48px' }}></i>
                    </div>
                    <h5 className="text-muted">No sandbox endpoints created yet</h5>
                    <p className="text-muted">Click "Create New Endpoint" to add your first sandbox endpoint.</p>
                </div>
            );
        }

        return (
            <div className="table-responsive">
                <Table className="table-nowrap align-middle mb-0">
                    <thead>
                        <tr>
                            <th scope="col">Actions</th>
                            <th scope="col">Title</th>
                            <th scope="col">Endpoint Slug</th>
                            <th scope="col">API Endpoint</th>
                            <th scope="col">Status</th>
                            <th scope="col">Published Date</th>
                            <th scope="col">Created</th>
                            <th scope="col">Last Updated</th>
                        </tr>
                    </thead>
                    <tbody>
                        {docProcessors.map((processor) => (
                            <tr key={processor.processorId}>
                                <td>
                                    <div className="d-flex gap-2">
                                        <Button
                                            color="info"
                                            size="sm"
                                            outline
                                            title="View Code Examples"
                                            onClick={() => handleShowCodeExamples(processor)}
                                        >
                                            <i className="ri-code-s-slash-line"></i>
                                        </Button>
                                        <Button
                                            color="primary"
                                            size="sm"
                                            outline
                                            title="Edit Endpoint"
                                            onClick={() => handleEditEndpoint(processor)}
                                        >
                                            <i className="ri-edit-line"></i>
                                        </Button>
                                        {processor.status === 1 && (
                                            <Button
                                                color="success"
                                                size="sm"
                                                outline
                                                title="Publish to Production"
                                                onClick={() => handlePublishEndpoint(processor)}
                                            >
                                                <i className="ri-upload-cloud-line"></i>
                                            </Button>
                                        )}
                                        <Button
                                            color="danger"
                                            size="sm"
                                            outline
                                            title="Delete Endpoint"
                                            onClick={() => handleDeleteEndpoint(processor)}
                                        >
                                            <i className="ri-delete-bin-line"></i>
                                        </Button>
                                    </div>
                                </td>
                                <td>
                                    <div>
                                        <h6 className="mb-1">{processor.title || 'Untitled'}</h6>
                                        <p className="text-muted mb-0 text-truncate" style={{ maxWidth: '200px' }}>
                                            {processor.description || 'No description'}
                                        </p>
                                    </div>
                                </td>
                                <td>
                                    <code className="bg-light px-2 py-1 rounded">
                                        {processor.endpointSlug}
                                    </code>
                                </td>
                                <td>
                                    <code className="text-primary">
                                        {`/v1/flow/${processor.endpointSlug}`}
                                    </code>
                                </td>
                                <td>
                                    {getStatusBadge(processor.status)}
                                </td>
                                <td>
                                    <span className="text-muted">
                                        {processor.publishedDate ? formatDate(processor.publishedDate) : 'Not published'}
                                    </span>
                                </td>
                                <td>
                                    <span className="text-muted">
                                        {processor.createdAt ? formatDate(processor.createdAt) : 'N/A'}
                                    </span>
                                </td>
                                <td>
                                    <span className="text-muted">
                                        {processor.updatedAt ? formatDate(processor.updatedAt) : 'N/A'}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
        );
    };

    return (
        <>
            <Card className="card-animate border-warning">
                <CardHeader className="d-flex align-items-center">
                    <div className="flex-grow-1">
                        <h4 className="card-title mb-0">
                            🟠 Sandbox Endpoints
                            {!isLoading && (
                                <span className="badge bg-soft-warning text-warning ms-2">
                                    {docProcessors.length}
                                </span>
                            )}
                        </h4>
                        <p className="text-muted mb-0 mt-1">
                            <small><strong>Base URL:</strong> <code>{process.env.REACT_APP_INTEGR_API_URL || 'https://api.integr.dev'}</code></small>
                        </p>
                    </div>
                    <div className="flex-shrink-0 d-flex gap-2">
                        <Button
                            color="light"
                            onClick={fetchDocProcessors}
                            disabled={isLoading}
                            title="Refresh"
                        >
                            <i className={`ri-refresh-line ${isLoading ? 'spinner-border spinner-border-sm' : ''}`}></i>
                        </Button>
                        <Button
                            color="info"
                            outline
                            onClick={() => setIsHelpModalOpen(true)}
                            title="How to create endpoints"
                        >
                            <i className="ri-question-line"></i>
                        </Button>
                        <Button
                            color="warning"
                            onClick={handleCreateEndpoint}
                            className="add-btn"
                        >
                            <i className="ri-add-line align-bottom me-1"></i>
                            Create New Endpoint
                        </Button>
                    </div>
                </CardHeader>
                <CardBody className="p-0">
                    {renderTableContent()}
                </CardBody>
            </Card>
            
            {/* Create/Edit Endpoint Modal */}
            <CreateEndpointModal
                isOpen={isCreateModalOpen}
                toggle={handleModalClose}
                projectId={projectId}
                envId={1} // Sandbox environment
                editData={selectedEndpoint}
                isEditMode={isEditMode}
            />
            
            {/* Publish Confirmation Modal */}
            <Modal isOpen={isPublishModalOpen} toggle={closePublishModal} centered>
                <ModalHeader toggle={closePublishModal}>
                    Publish Endpoint to Production
                </ModalHeader>
                <ModalBody>
                    <div className="text-center">
                        <div className="mb-3">
                            <i className="ri-upload-cloud-line text-success" style={{ fontSize: '48px' }}></i>
                        </div>
                        <h5 className="mb-3">Confirm Publishing</h5>
                        {endpointToPublish && (
                            <div className="text-muted mb-4">
                                <p className="mb-1"><strong>Title:</strong> {endpointToPublish.title}</p>
                                <p className="mb-1"><strong>Slug:</strong> {endpointToPublish.endpointSlug}</p>
                                <p className="mb-0"><strong>Current Environment:</strong> <Badge color="warning">Sandbox</Badge></p>
                            </div>
                        )}
                        <div className="alert alert-warning">
                            <i className="ri-information-line me-2"></i>
                            This will:
                            <ul className="text-start mt-2 mb-0">
                                <li>Create a new production version of this endpoint</li>
                                <li>Mark the current endpoint as "Published"</li>
                                <li>Assign a version number to the production release</li>
                                <li>Set the published date to today</li>
                            </ul>
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button 
                        color="light" 
                        onClick={closePublishModal}
                        disabled={isPublishing}
                    >
                        Cancel
                    </Button>
                    <Button 
                        color="success" 
                        onClick={confirmPublishEndpoint}
                        disabled={isPublishing}
                    >
                        {isPublishing ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Publishing...
                            </>
                        ) : (
                            <>
                                <i className="ri-upload-cloud-line me-1"></i>
                                Publish to Production
                            </>
                        )}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal isOpen={isDeleteModalOpen} toggle={closeDeleteModal} centered>
                <ModalHeader toggle={closeDeleteModal}>
                    Delete or Deactivate Endpoint
                </ModalHeader>
                <ModalBody>
                    <div className="text-center">
                        <div className="mb-3">
                            <i className="ri-delete-bin-line text-warning" style={{ fontSize: '48px' }}></i>
                        </div>
                        <h5 className="mb-3">What would you like to do with this endpoint?</h5>
                        {endpointToDelete && (
                            <div className="text-muted mb-4">
                                <p className="mb-1"><strong>Title:</strong> {endpointToDelete.title}</p>
                                <p className="mb-1"><strong>Slug:</strong> {endpointToDelete.endpointSlug}</p>
                            </div>
                        )}
                        <div className="row text-start">
                            <div className="col-md-6">
                                <div className="p-3 border rounded">
                                    <h6 className="text-warning mb-2">
                                        <i className="ri-pause-circle-line me-2"></i>
                                        Inactive Only
                                    </h6>
                                    <p className="text-muted small mb-0">
                                        Temporarily disable the endpoint. Can be reactivated later.
                                    </p>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="p-3 border rounded">
                                    <h6 className="text-danger mb-2">
                                        <i className="ri-delete-bin-line me-2"></i>
                                        Permanent Delete
                                    </h6>
                                    <p className="text-muted small mb-0">
                                        Permanently remove the endpoint. This cannot be undone.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter className="justify-content-between">
                    <Button 
                        color="light" 
                        onClick={closeDeleteModal}
                        disabled={isDeleting || isInactivating}
                    >
                        Cancel
                    </Button>
                    
                    <div className="d-flex gap-2">
                        <Button 
                            color="warning" 
                            onClick={handleInactiveEndpoint}
                            disabled={isDeleting || isInactivating}
                        >
                            {isInactivating ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Inactivating...
                                </>
                            ) : (
                                <>
                                    <i className="ri-pause-circle-line me-1"></i>
                                    Inactive Only
                                </>
                            )}
                        </Button>
                        <Button 
                            color="danger" 
                            onClick={confirmDeleteEndpoint}
                            disabled={isDeleting || isInactivating}
                        >
                            {isDeleting ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Deleting...
                                </>
                            ) : (
                                <>
                                    <i className="ri-delete-bin-line me-1"></i>
                                    Delete Permanently
                                </>
                            )}
                        </Button>
                    </div>
                </ModalFooter>
            </Modal>

            {/* Code Examples Modal */}
            <CodeExamplesModal
                isOpen={isCodeExamplesModalOpen}
                toggle={closeCodeExamplesModal}
                endpoint={endpointForCodeExamples}
            />

            {/* Help Modal */}
            <EndpointHelpModal
                isOpen={isHelpModalOpen}
                toggle={() => setIsHelpModalOpen(false)}
            />
        </>
    );
};

export default SandboxEndpointsTable;